import { FC, useEffect, useState } from 'react'
import { useAtomValue } from 'jotai'
import { ImageOverlay, TileLayer, useMap } from 'react-leaflet'
import { LatLngBounds, LatLngBoundsExpression } from 'leaflet'
import { currentMapAtom, mapImageAtom } from '@/js/editor/state/mapAtoms.ts'
import { useApi } from '@/js/editor/hooks/api/useApi.ts'

type MapLayerProps = {
    mapType: 'image' | 'geographic' | null
}

export const MapLayer: FC<MapLayerProps> = ({ mapType }) => {
    const [bounds, setBounds] = useState<LatLngBoundsExpression>()
    const mapImage = useAtomValue(mapImageAtom)
    const savedCenter = useAtomValue(currentMapAtom)?.center
    const map = useMap()
    const { apiEndpoint } = useApi()

    const imageUrl = mapImage ? `${apiEndpoint()}/storage/${mapImage}` : null

    useEffect(() => {
        setBounds(undefined)

        if (!imageUrl) {
            return
        }

        const image = new Image()

        const handleLoad = () => {
            const scaleFactor = image.height / 200
            const dimensions = {
                height: image.height / scaleFactor,
                width: image.width / scaleFactor,
            }

            const imageBounds = new LatLngBounds([
                [0, 0],
                [dimensions.height, dimensions.width],
            ])

            setBounds(imageBounds)

            if (mapType === 'image' && !savedCenter) {
                map.setView(imageBounds.getCenter(), map.getZoom())
            }
        }

        image.addEventListener('load', handleLoad)
        image.src = imageUrl

        return () => {
            image.removeEventListener('load', handleLoad)
        }
    }, [imageUrl, map, mapType, savedCenter])

    if (mapType === 'geographic') {
        return (
            <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
        )
    }

    if (imageUrl && bounds) {
        return <ImageOverlay url={imageUrl} bounds={bounds} />
    }

    if (!mapImage) {
        return (
            <div className={'absolute flex h-full w-full justify-center items-center text-mm-gray2'}>
                Keine Karte hinterlegt
            </div>
        )
    }

    return null
}
